// C01 ana sayfa — Hedef Termometresi + Çevirmen Şeridi + Kapanış Kartı.
//
// Veri akışı (hepsi canlı):
//   - Termometre yüzdesi: `total_with_termometer_assets_tl / effective_goal_tl × 100`
//     (Task 16: SADECE LİKİT — fonlar + termometer-flag'li other_assets.
//     Properties/Işık henüz satılmadığı için payda değil paydanın altında "ayrı").
//   - Birikim Dağılımı: likit breakdown + "Hedefe nakit ihtiyacı".
//   - Diğer Varlıklar: Işık + BES (ayrı tutulan asset'ler + büyük resim).
//   - Çevirmen şeridi: /translator/today (sinyal + endeks + işlem + anne).
//   - Kapanış kart tonu: pendingPushToday + portföy/hedef oranından kurallı.
//
// Token yoksa veya API hata verirse: tone default "rahat", termometre dim,
// şerit empty-state; rozetler 0.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPortfolioCombined,
  getGoalCurrent,
  getTranslatorToday,
  type ApiConfig,
  ApiError,
} from "../lib/api";
import type {
  GoalCurrent,
  PortfolioCombined,
  TranslatorEvent,
} from "../types/api";
import Termometre from "../components/Termometre";
import CevirmenSerit from "../components/CevirmenSerit";
import KapanisKart, { type KapanisTone } from "../components/KapanisKart";
import FreshnessIndicator from "../components/FreshnessIndicator";
import AnomalyBadge from "../components/AnomalyBadge";
import BirikimDagilimi from "../components/BirikimDagilimi";
import DigerVarliklar from "../components/DigerVarliklar";

interface Props {
  cfg: ApiConfig;
  /** Bugün değerlendirilmiş sinyal fund kodları (App state'inden gelir) */
  evaluatedFunds: Set<string>;
  /** r2: açık anomali sayısı — header'daki ⚠ rozet için */
  openAnomaliesCount: number;
}

function formatClock(d: Date): string {
  return d.toLocaleTimeString("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Kapanış tonu kuralı — basit v1:
 *   - acil:  bugün push signal > 0  VEYA  portföy/hedef oranı < 0.55
 *   - rahat: portföy/hedef oranı ≥ 0.6 VE bugün push signal == 0
 *   - sakin: aralık VEYA ratio null (veri yok)
 *
 * Veri yoksa "sakin" döner — "rahat" yanıltıcı (kullanıcı önündeymiş gibi).
 */
function deriveKapanisTone(
  ratio: number | null,
  pendingPushToday: number,
): KapanisTone {
  if (ratio === null) return "sakin";
  if (pendingPushToday > 0 || ratio < 0.55) return "acil";
  if (ratio >= 0.6 && pendingPushToday === 0) return "rahat";
  return "sakin";
}

interface DebugRow {
  path: string;
  status: number;
  ok: boolean;
  msg?: string;
}

export default function Home({ cfg, evaluatedFunds, openAnomaliesCount }: Props) {
  const [combined, setCombined] = useState<PortfolioCombined | null>(null);
  const [goal, setGoal] = useState<GoalCurrent | null>(null);
  const [events, setEvents] = useState<TranslatorEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [debug, setDebug] = useState<DebugRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [clock, setClock] = useState(() => formatClock(new Date()));
  const navigate = useNavigate();

  // Saat dakika başı güncellensin.
  useEffect(() => {
    const t = setInterval(() => setClock(formatClock(new Date())), 30_000);
    return () => clearInterval(t);
  }, []);

  async function load() {
    if (!cfg.base) {
      setErr(
        "API base ayarlanmamış. Ayarlar'dan API base + Bearer token gir.",
      );
      setDebug([]);
      return;
    }
    setLoading(true);
    setErr(null);
    const names: [string, () => Promise<unknown>][] = [
      ["/portfolio/combined", () => getPortfolioCombined(cfg)],
      ["/goal/current", () => getGoalCurrent(cfg)],
      ["/translator/today", () => getTranslatorToday(cfg)],
    ];
    const diag: DebugRow[] = [];
    try {
      const results = await Promise.all(
        names.map(([path, fn]) =>
          fn().then(
            (v) => ({ ok: true as const, path, value: v }),
            (e: unknown) => ({ ok: false as const, path, error: e }),
          ),
        ),
      );
      for (const r of results) {
        if (r.ok) {
          diag.push({ path: r.path, status: 200, ok: true });
        } else {
          const e = r.error;
          if (e instanceof ApiError) {
            diag.push({
              path: r.path,
              status: e.status,
              ok: false,
              msg: e.message,
            });
          } else {
            diag.push({
              path: r.path,
              status: 0,
              ok: false,
              msg: e instanceof Error ? e.message : String(e),
            });
          }
        }
      }
      setDebug(diag);

      const [c, g, t] = results;
      setCombined(c.ok ? (c.value as PortfolioCombined) : null);
      setGoal(g.ok ? (g.value as GoalCurrent) : null);
      setEvents(
        t.ok ? ((t.value as { items: TranslatorEvent[] }).items ?? []) : [],
      );

      const allFail = results.every((r) => !r.ok);
      const firstErr = results.find((r) => !r.ok);
      if (allFail && firstErr && !firstErr.ok) {
        const e = firstErr.error;
        if (e instanceof ApiError && e.status === 401) {
          setErr(
            "API'ye yetkisiz erişim (HTTP 401) — Ayarlar'daki Bearer token üretimdeki API_READ_TOKEN secret değeriyle birebir aynı olmalı. Baş/son boşluk + tam 64 hex karakter kontrol et.",
          );
        } else if (e instanceof ApiError) {
          setErr(
            `API yanıt vermedi (HTTP ${e.status}). Detay aşağıda.`,
          );
        } else {
          setErr(
            "API'ye bağlanılamadı (network/CORS). API base URL'ini ve internet bağlantısını kontrol et. Detay aşağıda.",
          );
        }
      }
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? `${e.message} (HTTP ${e.status})`
          : e instanceof Error
            ? e.message
            : String(e);
      setErr(msg);
      setDebug(diag);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (cfg.base) {
      void load();
    } else {
      // base yoksa direkt banner göster
      setErr("API base ayarlanmamış. Ayarlar'dan API base + Bearer token gir.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.base, cfg.token]);

  // Hedef + portföy oranı → termometre + tone.
  // Task 16: Termometre payı SADECE LİKİT (fonlar + termometer-flag'li other_assets).
  // Payda = effective_goal_tl (hedef_ev + likidite_tamponu) — Task 14'ten beri aynı.
  const hedefTl = goal?.hedef_tl ?? null;
  const effectiveGoalTl = combined?.combined.effective_goal_tl ?? null;
  const reserveTl = combined?.combined.liquidity_reserve_tl ?? null;
  const totalLiquid =
    combined?.combined.total_with_termometer_assets_tl ?? null;
  const ratio = useMemo(() => {
    if (combined === null) return null;
    // Backend tek otorite — yuvarlama tutarlı.
    if (combined.combined.goal_ratio_effective !== null) {
      return combined.combined.goal_ratio_effective;
    }
    // Fallback (effective hedef hesaplanamadıysa) — hiç olmaması beklenir.
    if (totalLiquid !== null && effectiveGoalTl !== null && effectiveGoalTl > 0) {
      return totalLiquid / effectiveGoalTl;
    }
    return null;
  }, [combined, effectiveGoalTl, totalLiquid]);

  const pct = ratio === null ? 0 : Math.max(0, Math.min(100, ratio * 100));

  // Bugünkü push signal sayısı — Çevirmen Şeridi'nden türetilebilir
  // (translator backend zaten notify=1 buy/sell'leri döndürüyor).
  const pendingPushToday = events.filter((e) => e.kind === "sinyal").length;

  const tone = deriveKapanisTone(ratio, pendingPushToday);

  function handleSignalClick(ev: TranslatorEvent) {
    if (ev.fund) navigate(`/sinyal/${ev.fund}`);
  }

  const anyEvaluated = events.some(
    (e) => e.kind === "sinyal" && !!e.fund && evaluatedFunds.has(e.fund),
  );

  return (
    <main className="min-h-screen flex flex-col mx-auto" style={{ maxWidth: 430 }}>
      <header className="flex items-center justify-between px-4 pt-5 pb-3">
        <div className="flex items-baseline gap-2">
          <h1 className="text-base font-semibold tracking-tight text-ink-100">
            AiFon
          </h1>
          <span className="text-ink-400 text-sm">·</span>
          <span className="text-ink-300 text-sm fon-dili">{clock}</span>
        </div>
        <div className="flex items-center gap-1">
          <AnomalyBadge
            count={openAnomaliesCount}
            onClick={() => navigate("/anomaliler")}
          />
          <button
            type="button"
            aria-label="Ayarlar"
            className="text-ink-300 hover:text-ink-100 p-2 -mr-2"
            onClick={() => navigate("/ayarlar")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1A2 2 0 1 1 7 4.6l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
            </svg>
          </button>
        </div>
      </header>

      <div className="px-4 pb-2">
        <FreshnessIndicator
          lastPriceDate={combined?.last_price_date ?? "—"}
          indexPeriod={goal?.period ?? "—"}
          signalRefreshedAt={combined?.ts ?? ""}
          mock={!combined && !goal}
        />
      </div>

      {err && (
        <div className="mx-4 mb-2 rounded-xl border border-warn-500/50 bg-warn-500/10 p-3 text-[12px]">
          <p className="text-warn-400 mb-1 font-medium">Veri çekilemedi</p>
          <p className="text-ink-200 break-words">{err}</p>

          {/* cfg snapshot — token uzunluğu + base URL gösterilir, token gizli */}
          <div className="mt-2 text-[10px] fon-dili text-ink-400 space-y-0.5">
            <p>
              base: <span className="text-ink-200">{cfg.base || "(boş)"}</span>
            </p>
            <p>
              token:{" "}
              <span className="text-ink-200">
                {cfg.token ? `${cfg.token.length} karakter` : "(boş)"}
              </span>
            </p>
          </div>

          {/* Her endpoint'in tam durumu */}
          {debug.length > 0 && (
            <ul className="mt-2 text-[10px] fon-dili space-y-0.5">
              {debug.map((d) => (
                <li
                  key={d.path}
                  className={d.ok ? "text-gain-400" : "text-loss-400"}
                >
                  {d.ok ? "✓" : "✗"} {d.path} → HTTP {d.status}
                  {d.msg && <span className="text-ink-300"> · {d.msg}</span>}
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-2 mt-3">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => navigate("/ayarlar")}
            >
              Ayarlar →
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={load}
              disabled={loading}
            >
              Tekrar dene
            </button>
          </div>
        </div>
      )}

      <section
        className="px-4 pt-2 pb-3 flex items-stretch gap-3 flex-col-mobile section-pair"
        aria-label="Hedef ve günün çevirisi"
      >
        <Termometre
          pct={pct}
          effectiveGoalTl={effectiveGoalTl}
          evGoalTl={hedefTl}
          reserveTl={reserveTl}
        />
        <CevirmenSerit
          events={events}
          onSignalClick={handleSignalClick}
          evaluatedFunds={evaluatedFunds}
        />
      </section>

      <KapanisKart
        tone={tone}
        evaluatedAny={anyEvaluated}
        pendingPushToday={pendingPushToday}
        ratio={ratio}
      />

      {/* Task 16 — Likit Birikim (fonlar + termometer-flag'li) + Hedefe nakit ihtiyacı */}
      <BirikimDagilimi combined={combined} />

      {/* Task 16 — Ayrı Tutulan (Işık + BES) + Toplam Varlık (büyük resim) */}
      <DigerVarliklar combined={combined} />

      {/* r2 — yeni route'lar için keşfedilebilir giriş */}
      <section className="px-4 mt-5">
        <h3 className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold px-1 mb-2">
          Detay
        </h3>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => navigate("/anomaliler")}
            className="w-full text-left rounded-xl bg-ink-800 border border-ink-700 hover:border-warn-500/60 hover:bg-ink-700/40 transition px-4 py-3 flex items-center gap-3"
          >
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-warn-500/15 text-warn-400"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" />
              </svg>
            </span>
            <div className="grow min-w-0">
              <p className="text-ink-100 text-sm font-medium">Anomalileri Gör</p>
              <p className="text-[11px] text-ink-400 mt-0.5">
                {openAnomaliesCount} açık · Karantina sayfası
              </p>
            </div>
            <span className="text-ink-500" aria-hidden="true">
              ›
            </span>
          </button>

          <button
            type="button"
            onClick={() => navigate("/sinyaller")}
            className="w-full text-left rounded-xl bg-ink-800 border border-ink-700 hover:border-accent-500/60 hover:bg-ink-700/40 transition px-4 py-3 flex items-center gap-3"
          >
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-accent-500/15 text-accent-300"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12h4l3-9 4 18 3-9h4" />
              </svg>
            </span>
            <div className="grow min-w-0">
              <p className="text-ink-100 text-sm font-medium">Sinyal Listesi</p>
              <p className="text-[11px] text-ink-400 mt-0.5">
                Push + low-score · iki katmanlı
              </p>
            </div>
            <span className="text-ink-500" aria-hidden="true">
              ›
            </span>
          </button>

          <button
            type="button"
            onClick={() => navigate("/anne")}
            className="w-full text-left rounded-xl bg-ink-800 border border-ink-700 hover:border-calm-500/60 hover:bg-ink-700/40 transition px-4 py-3 flex items-center gap-3"
          >
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-calm-500/15 text-calm-400"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
              </svg>
            </span>
            <div className="grow min-w-0">
              <p className="text-ink-100 text-sm font-medium">Anne Defteri</p>
              <p className="text-[11px] text-ink-400 mt-0.5">
                Üç cümle · ev senaryosu · preflight
              </p>
            </div>
            <span className="text-ink-500" aria-hidden="true">
              ›
            </span>
          </button>

          <button
            type="button"
            onClick={() => navigate("/es")}
            className="w-full text-left rounded-xl bg-ink-800 border border-ink-700 hover:border-accent-500/60 hover:bg-ink-700/40 transition px-4 py-3 flex items-center gap-3"
          >
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-accent-500/15 text-accent-300"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </span>
            <div className="grow min-w-0">
              <p className="text-ink-100 text-sm font-medium">Eş Portföyü</p>
              <p className="text-[11px] text-ink-400 mt-0.5">
                Sanal · manuel giriş · hedef katkısı
              </p>
            </div>
            <span className="text-ink-500" aria-hidden="true">
              ›
            </span>
          </button>
        </div>
      </section>

      <footer className="mt-auto px-4 pb-6 pt-4 text-center text-[11px] text-ink-400">
        AiFon · {new Date().getFullYear()}
        {combined && (
          <span className="ml-1">
            · likit{" "}
            {Math.round(
              combined.combined.total_with_termometer_assets_tl ??
                combined.combined.total_value_tl,
            ).toLocaleString("tr-TR")}{" "}
            TL · varlık{" "}
            {Math.round(
              combined.combined.total_assets_tl ??
                combined.combined.total_value_tl,
            ).toLocaleString("tr-TR")}{" "}
            TL
          </span>
        )}
      </footer>
    </main>
  );
}
